import { motion } from "framer-motion";

const PRODUCT_IMG = "https://customer-assets.emergentagent.com/job_melting-moments-1/artifacts/vzk6h7ae_BDE40D93-2E49-40E1-8667-F62DB9C1E7EA.png";
const DARK_DETAIL = "https://images.unsplash.com/photo-1679143121414-3fdf2479189d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwY2hvY29sYXRlJTIwbHV4dXJ5JTIwcHJlbWl1bSUyMGFydGlzYW58ZW58MHx8fHwxNzc2MTc2Mzg3fDA&ixlib=rb-4.1.0&q=85";
const TRUFFLE_IMG = "https://images.pexels.com/photos/11178470/pexels-photo-11178470.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const ease = [0.16, 1, 0.3, 1];

const products = [
  {
    id: 1,
    name: "Noir & Ivoire",
    subtitle: "The Classic Pair",
    price: "\u20B94,999",
    pairA: "72% Single-Origin Dark",
    pairB: "Tahitian Vanilla White",
    description: "Where intensity meets innocence. Our signature yin-yang pairing — dark brooding cacao alongside silken vanilla bean white.",
    image: PRODUCT_IMG,
  },
  {
    id: 2,
    name: "Fuego & Frost",
    subtitle: "The Unexpected Pair",
    price: "\u20B93,499",
    pairA: "Habanero Chili Dark",
    pairB: "Cool Peppermint Cream",
    description: "Fire on the left, ice on the right. A pairing that proves opposites don't just attract — they ignite.",
    image: TRUFFLE_IMG,
  },
  {
    id: 3,
    name: "Rose & Ember",
    subtitle: "The Poetic Pair",
    price: "\u20B94,299",
    pairA: "Persian Rose Milk",
    pairB: "Smoked Espresso Dark",
    description: "Soft floral romance meets charred intensity. Two chocolates, one love letter. Crafted for those who feel deeply.",
    image: DARK_DETAIL,
  },
];

export default function ProductShowcase() {
  return (
    <section
      id="product-showcase"
      data-testid="product-showcase-section"
      className="pt-10 md:pt-14 pb-24 md:pb-32 px-6 md:px-12 lg:px-24 bg-[#050505]"
    >
      {/* Section header */}
      <div className="text-center mb-20 md:mb-28">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease }}
          className="font-body text-xs md:text-sm uppercase tracking-[0.2em] text-[#C6A96B] mb-6"
        >
          Always in Pairs. Never Alone.
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, delay: 0.2, ease }}
          className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#FDFBF7] mb-6"
        >
          The Pairings
        </motion.h2>
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: 60 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.4, ease }}
          className="h-px bg-[#C6A96B] mx-auto mb-8"
        />
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.6, ease }}
          className="font-body text-sm md:text-base font-light text-[#A3A3A3] max-w-lg mx-auto leading-relaxed"
        >
          Every box holds two. Two flavours, two moods, two halves of one story.
          Because at YY Atelier, chocolate is never meant to be experienced alone.
        </motion.p>
      </div>

      {/* Product grid */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-16">
        {products.map((product, i) => (
          <motion.div
            key={product.id}
            data-testid={`product-card-${product.id}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: i * 0.2, ease }}
            className="group cursor-pointer"
          >
            {/* Image */}
            <div className="relative overflow-hidden mb-8 aspect-[3/4]">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-[#050505]/20 group-hover:bg-[#050505]/10 transition-all duration-700" />
              <div className="absolute bottom-0 left-0 w-0 h-px bg-[#C6A96B] group-hover:w-full transition-all duration-700" />
            </div>

            {/* Text */}
            <div className="space-y-3">
              <p className="font-body text-xs uppercase tracking-[0.2em] text-[#C6A96B]">
                {product.subtitle}
              </p>
              <h3 className="font-heading text-2xl font-light text-[#FDFBF7] tracking-normal">
                {product.name}
              </h3>

              {/* Pair labels */}
              <div className="flex items-center gap-3 py-2">
                <span className="font-body text-xs text-[#FDFBF7]/70 tracking-wide">
                  {product.pairA}
                </span>
                <span className="text-[#C6A96B] text-xs">&</span>
                <span className="font-body text-xs text-[#FDFBF7]/70 tracking-wide">
                  {product.pairB}
                </span>
              </div>

              <p className="font-body text-sm font-light text-[#A3A3A3] leading-relaxed">
                {product.description}
              </p>
              <div className="flex items-center justify-between pt-4 border-t border-[#1F1F1F]">
                <span className="font-body text-lg text-[#C6A96B] font-light tracking-wide">
                  {product.price}
                </span>
                <button
                  data-testid={`add-to-cart-${product.id}`}
                  className="font-body text-xs uppercase tracking-[0.2em] text-[#A3A3A3] hover:text-[#C6A96B] transition-colors duration-500 bg-transparent border-none cursor-pointer"
                >
                  Discover the Pair
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
